import React from 'react';
// Leave React here for JSX support in send/receive information

/*
    Note about channel capabilities

    If a capability has send/receive, you can pass information that will appear as a popover using `sendInformation` and `receiveInformation`.
    If a capability has just a generic support level, you can make it an object and pass `level` for the support level and `information`
    for the popover content.

    {
        send: SUPPORT_LEVEL.FULL,
        receive: SUPPORT_LEVEL.FULL,
        sendInformation: 'Some info in text',
        receiveInformation: <div>Some info in JSX</div>
    }

    {
        level: SUPPORT_LEVEL.FULL,
        information: 'Some info text'
    }

    The `capabilities` property of the channelData object is also used to populate the cards and modals visible in the channel pages. Here's a full description of the additional entry:

    {
        subtitle: 'high level description of the capability',
        description: 'additional information for the capability displayed on the modal',
        APILink: 'URL to the API documentation',
        docsLink: 'URL to the docs documentation',
        screenshot: require('path to screenshot image of the capability'),
        screenshotVisibleArea: 'top' or 'bottom'
    }
*/

import { channelDataV1 } from './channelCapabilitiesV1';
import { merge } from 'lodash';

export const SUPPORT_LEVEL = {
    FULL: 'full',
    PARTIAL: 'partial',
    PARTIAL_HOSTED: 'partial_hosted',
    NONE: 'none',
    NA: 'na',
};

export const UNSUPPORTED_DESCRIPTION = 'Not natively supported';

export const CAPABILITIES_TYPES = [
    'contentTypes',
    'actionTypes',
    'structuredMessages',
    'events',
    'accountLinking',
    'deliveryEvents',
    'outboundMessaging',
];

export const CAPABILITIES_TYPES_DETAILS = {
    contentTypes: {
        list: ['text', 'image', 'audio', 'video', 'file', 'emoji', 'gif', 'location'],
        name: 'Content Types',
    },
    actionTypes: {
        list: ['link', 'webview', 'buy', 'postback', 'replies', 'locationRequest'],
        name: 'Action Types',
    },
    structuredMessages: {
        list: ['compoundMessages', 'carousel', 'form'],
        name: 'Structured Messages',
    },
    events: {
        list: ['typingIndicator', 'readIndicator', 'conversationStart'],
        name: 'Indicators',
    },
    deliveryEvents: {
        list: ['channelDeliveryEvents', 'userDeliveryEvents', 'channelMessageIds'],
        name: 'Delivery events',
    },
    accountLinking: {
        list: ['webMessengerLinking', 'APILinking', 'linkingRequests'],
        name: 'Account Linking',
    },
    notificationCapabilities: {
        list: ['pushCapable'],
        name: 'Notification Capabilities',
    },
    outboundMessaging: {
        list: ['notificationEvents'],
        name: 'Outbound Messaging',
    },
};

export const CAPABILITIES = {
    text: {
        name: 'Text',
        link: '/guide/sending-messages/#sending-text-messages-with-the-api',
        send: true,
        receive: true,
    },
    image: {
        name: 'Image',
        link: '/guide/structured-messages/#images-stickers-and-gifs',
        send: true,
        receive: true,
    },
    file: {
        name: 'File',
        link: 'https://docs.smooch.io/rest/#operation/postMessage',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    audio: {
        name: 'Audio',
        link: 'https://docs.smooch.io/rest/#operation/postMessage',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    video: {
        name: 'Video',
        link: 'https://docs.smooch.io/rest/#operation/postMessage',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    gif: {
        name: 'GIF',
        link: '/guide/structured-messages/#images-stickers-and-gifs',
        send: true,
        receive: true,
    },
    emoji: {
        name: 'Emoji',
        send: true,
        receive: true,
    },
    link: {
        name: 'Link',
        link: '/guide/structured-messages/#link-buttons',
        send: true,
        receive: false,
    },
    webview: {
        name: 'Extension',
        link: '/guide/conversation-extensions/',
        send: true,
        receive: false,
    },
    buy: {
        name: 'Buy',
        link: '/guide/structured-messages/#buy-buttons',
        send: true,
        receive: false,
    },
    postback: {
        name: 'Postback',
        link: '/guide/structured-messages/#postback-buttons',
        send: true,
        receive: false,
    },
    replies: {
        name: 'Reply',
        link: '/guide/structured-messages/#reply-buttons',
        send: true,
        receive: false,
    },
    compoundMessages: {
        name: 'Compound Message',
        link: '/guide/structured-messages/#compound-message',
        send: true,
        receive: false,
    },
    carousel: {
        name: 'Carousel',
        link: '/guide/structured-messages/#carousel-messages',
        send: true,
        receive: false,
    },
    form: {
        name: 'Form',
        link: '/guide/structured-messages/#forms',
        send: true,
        receive: false,
    },
    list: {
        name: 'List',
        link: '/guide/structured-messages/#list-messages',
        send: true,
        receive: false,
    },
    typingIndicator: {
        name: 'Typing',
        link: '/guide/sending-messages/#sending-typing-activity-with-the-api',
        send: true,
        receive: true,
    },
    readIndicator: {
        name: 'Read',
        send: true,
        receive: true,
    },
    conversationStart: {
        name: 'Conversation Start',
        send: false,
        receive: true,
    },
    webMessengerLinking: {
        name: 'via Web Messenger',
        link: '/guide/web-messenger#alternate-channels',
        send: false,
        receive: false,
    },
    APILinking: {
        name: 'via API',
        link: 'https://docs.smooch.io/rest/#operation/createClient',
        isExternalLink: true,
        send: false,
        receive: false,
    },
    linkingRequests: {
        name: 'via Link Requests',
        link: 'https://docs.smooch.io/rest/v1/#get-link-requests',
        isExternalLink: true,
        send: false,
        receive: false,
    },
    location: {
        name: 'Location',
        send: true,
        receive: true,
    },
    locationRequest: {
        name: 'Location Request',
        link: '/guide/structured-messages/#location-request-buttons',
        send: true,
    },
    channelDeliveryEvents: {
        name: 'Delivered to channel',
        link: '/guide/delivery-events',
        receive: true,
    },
    userDeliveryEvents: {
        name: 'Delivered to user',
        link: '/guide/delivery-events',
        receive: true,
    },
    channelMessageIds: {
        name: 'External messages are included in the delivery event',
        receive: true,
    },
    pushCapable: {
        name: 'Push capable',
        link: '/guide/sending-messages/#automatic-message-delivery',
    },
    notificationEvents: {
        name: 'Notification',
        link: '/guide/outbound-messaging',
        send: true,
    },
};

const v2SpecificChannelData = {
    ios: {
        description:
            'The Sunshine Conversations iOS SDK is a highly customizable chat embeddable that can be added to any iOS app. It supports the Sunshine Conversations API <a href="/guide/native-ios-sdk/#capabilities">capabilities</a>, <a href="/guide/native-ios-sdk/#configuring-push-notifications">push notifications</a> and is fully <a href="/guide/native-ios-sdk/#localization">localized</a>.',
        guideUrl: '/guide/native-ios-sdk/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'The iOS SDK displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
                description:
                    'When including replies with a message, the iOS SDK  will display them at the bottom of the conversation. Users can quickly select one of them to send that reply.',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            list: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#list-messages',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
                description:
                    'The iOS SDK can show that the agent is typing with a typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/#typing-events">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'The iOS SDK can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/#conversation-events">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Confirm the delivery of a message to the user',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NA,
            linkingRequests: SUPPORT_LEVEL.NA,
        },
    },
    android: {
        description:
            'The Sunshine Conversations android SDK is a highly customizable chat embeddable that can be added to any Android app. It supports all the Sunshine Conversations API <a href="/guide/native-android-sdk/#capabilities">capabilities</a>, <a href="/guide/native-android-sdk/#configuring-push-notifications">push notifications</a> and is fully <a href="/guide/native-android-sdk/#localization">localized</a>.',
        headerImg: require('../images/capabilities-view/android/welcome.png'),
        guideUrl: '/guide/native-android-sdk/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
                description:
                    'The conversation interface supports the display of various image types. Tapping on an image will open it in an image viewer.',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
                sendInformation: 'GIFs sent via the API will be animated',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'The Android SDK displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
                description:
                    'The Android SDK can show that the agent is typing with an typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'The Android SDK can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when a user has read the conversation.',
            },
            conversationStart: {
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Confirm the delivery of a message to the user',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NA,
            linkingRequests: SUPPORT_LEVEL.NA,
        },
    },
    web: {
        description:
            'The Sunshine Conversations Web Messenger is a highly customizable messaging widget that can be added to any web page. Web Messenger is unique in that it allows the user seamlessly <a href="/guide/web-messenger/#moving-to-other-channels">move the conversation</a> beyond the browser to a persistent channel like SMS, Facebook Messenger, or any other <a href="/guide/intro-to-channels/">OTT channels.</a>',
        guideUrl: '/guide/web-messenger/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'Web Messenger displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/conversation-extensions/">our guide</a> for more details. </br> Extensions can be set to open automatically. See <a href="https://docs.smooch.io/rest/#operation/postMessage">our API documentation</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            form: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#forms',
                description:
                    'Forms support up to a maximum of 100 fields. Each field must include a type, name and label. Optionally, <code>minSize</code>, <code>maxSize</code> and <code>placeholder</code> can be specified depending on the field type. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            list: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#list-messages',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
                description:
                    'Web Messenger can show that the agent is typing with an typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'Web Messenger can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NA,
            linkingRequests: SUPPORT_LEVEL.NA,
        },
    },
    twilio: {
        headerLinks: {
            'Twilio API Docs': 'https://www.twilio.com/docs/sms/api',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/twilio-sms/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'Conversation extensions are delivered via hyperlink to SMS clients. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text on SMS.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 compound message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally a description can be included as well. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.FULL,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    messagebird: {
        headerLinks: {
            'MessageBird API Docs': 'https://developers.messagebird.com/docs/sms-messaging',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/messagebird-sms/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'In MessageBird SMS, the user can open a custom experience in their browser via a link. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postbacks button</a> can trigger any logic on your server, which can then update the conversation back using the API.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally a description can be included as well. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.FULL,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    telegram: {
        headerLinks: {
            'Telegram API Docs': 'https://core.telegram.org/bots',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/telegram/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'In Telegram, the user can open a custom experience in their browser via a link. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
                description:
                    'Telegram can display to the user that the agent is typing. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'Receive a <code>conversation:read</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user reads the conversation',
            },
            conversationStart: {
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.FULL,
        },
    },
    messenger: {
        headerLinks: {
            'Official Messenger Docs': 'https://developers.facebook.com/docs/messenger-platform',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/facebook-messenger/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'Messenger displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one supported action. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'Messenger can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.FULL,
        },
    },
    line: {
        headerLinks: {
            'Official LINE Docs': 'https://developers.line.me/en/docs/messaging-api/overview/',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/line/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'On LINE, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    wechat: {
        headerLinks: {
            'Official WeChat Docs': 'https://developers.weixin.qq.com/doc/offiaccount/en/Getting_Started/Overview.html',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/wechat/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'On WeChat conversation extensions are displayed in a browser. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text suggestions on WeChat.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    viber: {
        headerLinks: {
            'Official Viber Docs': 'https://developers.viber.com/docs/',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/viber/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'In Viber, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postbacks</a> are sent to Viber as predefined text that when sent by the user as a message notify the server by webhook. The server can then act on the event and post messages back to the user in response.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                subtitle: 'Compose messages with multiple actions',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>.',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'Receive a <code>conversation:read</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user reads the conversation',
            },
            conversationStart: {
                subtitle:
                    'Receive a <code>conversation:create</code> <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> when the user starts the conversation',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.FULL,
        },
    },
    twitter: {
        headerLinks: {
            'Official Twitter Docs': 'https://developer.twitter.com/en/docs/direct-messages/api-features',
            'Integration API': 'https://docs.smooch.io/rest/#operation/createIntegration',
        },
        guideUrl: '/guide/twitter/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            gif: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            emoji: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                description:
                    'On Twitter, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/#operation/postMessage">API documentation</a>.',
            },
            typingIndicator: {
                description:
                    'A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is available to signal when a user is typing.',
            },
            readIndicator: {
                description:
                    'A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is available to signal when a user reads the latest message in a conversation.',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    apple: {
        guideUrl: '/guide/apple-messages-for-business/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
                send: SUPPORT_LEVEL.FULL,
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            list: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#list-messages',
            },
            typingIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                docsLink: '/guide/sending-messages/#sending-typing-activity-with-the-api',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.NONE,
        },
    },
    instagram: {
        icon: require('../images/channel-icons/instagram.png'),
        headerIcon: '/images/channel-header-logos/logo_instagram.png',
        brandColor: '#DD2A7B',
        name: 'Instagram',
        description:
            'Instagram Direct is a channel that allows businesses with Professional Instagram accounts to surface their story replies, direct messages, and mentions in their preferred platform. With Instagram, businesses have the opportunity to drive sales and improve customer satisfaction by having meaningful interactions in a scalable manner.',
        headerImg: '/images/instagram_tmp.png',
        headerLinks: {
            'Official Instagram docs': 'https://developers.facebook.com/products/messenger/messenger-api-instagram',
        },
        guideUrl: '/guide/instagram/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/instagram/text.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/instagram/image.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
                description:
                    'Instagram allows users to send and receive images. Images can be also be forwarded to other users.',
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/instagram/file.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
                subtitle: 'Receive a link to a file',
                description:
                    'Files sent by Sunshine Conversations are rendered for the user as a link that can be clicked on and opened.',
            },
            audio: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/instagram/audio.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
                subtitle: 'Send and receive rich audio messages',
                description:
                    'Instagram allows users to record and send audio files. Audio files sent by Sunshine Conversations are rendered as a link that can be clicked on and opened.',
            },
            video: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/instagram/video.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
                subtitle: 'Receive a link to a video file',
                description:
                    'Video files sent by Sunshine Conversations are rendered for the user as a link that can be clicked on and opened.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/instagram/emoji.jpg'),
                screenshotVisibleArea: 'bottom',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Instagram displays any unicode emoji sent in text messages. Users can use the emoji keyboard on their device to send them.',
            },
            gif: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                screenshotVisibleArea: 'bottom',
                screenshot: require('../images/capabilities-view/instagram/gif.jpg'),
                subtitle: 'Send animated GIFs',
                description: 'GIFs are rendered as a static image.',
            },
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/instagram/location.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                description:
                    'Upon sending a location from Sunshine Conversations, the user will see a map of that location. Location messages include longitude and latitude coordinates along with a Google Maps link.',
            },
            link: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/instagram/link.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description:
                    'Upon sending a Link actions from Sunshine Conversations, the user will see the link as well as an image with a preview.',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/instagram/extensions.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description:
                    'Upon sending a webview action from Sunshine Conversations, the user will see the link to the fallback URI as well as an image with a preview.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/instagram/buy.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description:
                    'Upon sending a buy action rom Sunshine Conversations, the user will see a link that can be clicked on and opened.',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/instagram/postback.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description: 'Postback buttons are rendered as text on Instagram',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/instagram/reply.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'Replies are rendered as buttons on Instagram. All Instagram platforms support the Reply action type except for Web.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/instagram/compound.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                subtitle: 'Compose messages with text, an image and multiple actions',
                description: 'Compound messages are split into multiple messages on Instagram',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/instagram/carousel.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send a horizontally scrollable set of cards that can contain text, image, and action buttons',
                description: 'Carousels are rendered as text on Instagram. Only one image is displayed per message.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NA,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#list-messages',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
            },
            conversationStart: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
            },
            webMessengerLinking: SUPPORT_LEVEL.NONE,
            APILinking: SUPPORT_LEVEL.NONE,
            linkingRequests: SUPPORT_LEVEL.NONE,
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                sendInformation: 'Location requests are rendered as text',
                screenshot: require('../images/capabilities-view/instagram/location-request.jpg'),
                screenshotVisibleArea: 'bottom',
                docsLink: '/guide/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                description: 'Location request are rendered as text on instagram',
            },
            userDeliveryEvents: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelMessageIds: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
            },
        },
    },
    whatsapp: {
        guideUrl: '/guide/whatsapp/',
        capabilities: {
            text: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            image: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#images-stickers-and-gifs',
            },
            file: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            audio: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/sending-messages/',
            },
            location: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            link: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#link-buttons',
            },
            webview: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/conversation-extensions/',
            },
            buy: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/stripe-payments/',
                description:
                    'Enabled by our <a href="/guide/stripe-payments/">Stripe integration</a>, securely receive one-time payments',
            },
            postback: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#postback-buttons',
                description:
                    '<a href="/guide/structured-messages/#postback-buttons">Postback buttons</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text on WhatsApp',
            },
            replies: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#reply-buttons',
                send: SUPPORT_LEVEL.FULL,
                sendInformation: null,
            },
            compoundMessages: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            carousel: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#carousels-and-lists',
            },
            list: {
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
                docsLink: '/guide/structured-messages/#list-messages',
            },
            readIndicator: {
                APILink: 'https://docs.smooch.io/rest/#operation/postActivity',
                description:
                    'Receive events when the user has read your messages. A <a href="https://docs.smooch.io/rest/#operation/eventWebhooks">webhook event</a> is also available to signal when a user reads a conversation. WhatsApp can\'t show that the agent has read the conversation.',
            },
            APILinking: SUPPORT_LEVEL.FULL,
            linkingRequests: SUPPORT_LEVEL.FULL,
            locationRequest: {
                docsLink: '/guide/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/#operation/postMessage',
            },
            userDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
            channelDeliveryEvents: {
                docsLink: '/guide/delivery-events',
                APILink: 'https://docs.smooch.io/rest/#operation/eventWebhooks',
            },
        },
    },
};

export const channelData = merge(channelDataV1, v2SpecificChannelData);

export const CHANNELS = [
    'ios',
    'android',
    'web',
    'twilio',
    'messagebird',
    'telegram',
    'messenger',
    'twitter',
    'line',
    'wechat',
    'viber',
    'whatsapp',
    'apple',
    'instagram',
].map((id) =>
    Object.assign(
        {
            id,
        },
        channelData[id]
    )
);



// WEBPACK FOOTER //
// ./src/data/channelCapabilities.js